/**
 * Client-Side Authentication Service
 *
 * With Redis sessions, sensitive data (tokens) is stored server-side in Redis.
 * The client only has access to the session ID cookie (HttpOnly) and
 * the session_ok performance hint cookie.
 *
 * Client-side auth checks rely on checking for the session_ok cookie
 * or the existence of the session cookie via middleware behavior.
 */

/**
 * Interface for user data returned from Django backend
 */
export interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  profile_image_url: string | null;
  full_name: string;
  account_type: string;
  is_active: boolean;
}

/**
 * Interface for tenant context returned from Django backend
 */
export interface TenantContext {
  tenant_id: string;
  schema_name: string;
  business_name: string | null;
}

/**
 * Interface for Django login response (now only contains non-sensitive data)
 */
export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
  tenant_context: TenantContext;
}

import { COOKIES_NAME_CONFIG } from "@/config";
import { CLEAR_COOKIES_ENDPOINT_NEXT_SERVER } from "@/config/endpoints/apiEndpoints";

/**
 * Helper to parse cookies from document.cookie
 */
function parseCookies(): Record<string, string> {
  if (typeof document === "undefined") return {};
  return document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      if (key) acc[key] = value || "";
      return acc;
    },
    {} as Record<string, string>,
  );
}

/**
 * Client-side authentication check.
 * With Redis sessions, we check for the session_ok performance cookie
 * which is set by middleware after validating the Redis session.
 * The actual session validation happens server-side in middleware.
 */
export const isAuthenticatedUnified = async (): Promise<boolean> => {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    const cookies = parseCookies();

    // Check for session_ok cookie (set by middleware after Redis validation)
    // This is a non-HttpOnly performance hint cookie
    if (cookies["session_ok"] === "true") {
      return true;
    }

    // If no session_ok cookie, we can't directly check the HttpOnly session cookie.
    // The middleware handles the actual session validation.
    // If we're on a protected page and got here, middleware already validated.
    return false;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

/**
 * Client-side synchronous version for immediate checks.
 * Checks for the session_ok performance hint cookie.
 */
export const isAuthenticatedSync = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const cookies = parseCookies();
    return cookies["session_ok"] === "true";
  } catch (error) {
    console.error("Error in sync auth check:", error);
    return false;
  }
};

/**
 * Clear all authentication data.
 * With Redis sessions, this clears cookies via the server API which also
 * deletes the Redis session.
 */
export const clearAllAuthData = async (): Promise<void> => {
  try {
    // Clear any remaining localStorage data
    if (typeof window !== "undefined") {
      const authKeys = [
        COOKIES_NAME_CONFIG.ACCESS_TOKEN,
        COOKIES_NAME_CONFIG.REFRESH_TOKEN,
        COOKIES_NAME_CONFIG.USER_DATA,
      ];
      authKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    }

    // Clear cookies via API (also deletes Redis session)
    try {
      const { fetchWithXSRF } = await import("@/lib/fetchWithXSRF");
      await fetchWithXSRF(CLEAR_COOKIES_ENDPOINT_NEXT_SERVER, {
        method: "POST",
      });
    } catch (cookieError) {
      console.error("Failed to clear cookies:", cookieError);
    }
  } catch (error) {
    console.error("Error clearing all auth data:", error);
  }
};

/**
 * Get user data from cookies (client-side).
 * Note: With Redis sessions, user data is stored server-side.
 * This function checks cookies for any cached user data.
 */
export const getUserDataUnified = (): User | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cookies = parseCookies();
    const userData = cookies[COOKIES_NAME_CONFIG.USER_DATA];
    if (userData) {
      return JSON.parse(decodeURIComponent(userData));
    }

    // Fallback to localStorage
    const stored = localStorage.getItem(COOKIES_NAME_CONFIG.USER_DATA);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
};

/**
 * Get tenant context (client-side)
 */
export const getTenantContextUnified = (): TenantContext | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cookies = parseCookies();
    const tenantContext = cookies[COOKIES_NAME_CONFIG.TENANT_CONTEXT];
    if (tenantContext) {
      return JSON.parse(decodeURIComponent(tenantContext));
    }

    const stored = localStorage.getItem(COOKIES_NAME_CONFIG.TENANT_CONTEXT);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Error getting tenant context:", error);
    return null;
  }
};

/**
 * Get tenant ID (client-side)
 */
export const getTenantId = (): string | null => {
  const tenantContext = getTenantContextUnified();
  return tenantContext?.tenant_id || null;
};
